import { useState } from "react";

import {
  GeoJsonProperties,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon
} from "geojson";

import { ArrowDownIcon, ArrowUpIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";
import { DataSchemaType, DatasetAttributeSchema } from "@/types/dataset";
import { geoJSONToWKT } from "@/utils/wkt";

interface ComparePanelValueProps {
  value: GeoJsonProperties;
  attribute: DatasetAttributeSchema;
  geometry:
    | Point
    | MultiPoint
    | LineString
    | MultiLineString
    | Polygon
    | MultiPolygon;
  isPinned: boolean;
  index: number;
  attributeIndex: number;
  maxCollapsedLength?: number;
}

export default function ComparePanelValue({
  value,
  geometry,
  isPinned,
  index,
  attributeIndex,
  attribute,
  maxCollapsedLength = 200
}: Readonly<ComparePanelValueProps>) {
  const [expanded, setExpanded] = useState(false);
  const isGeometry = attribute.data_type === DataSchemaType.GEOMETRY;

  const valueAsText = isGeometry
    ? geoJSONToWKT(geometry)
    : String(value?.[attribute.attribute_name] ?? "");

  const shouldCollapse =
    isGeometry && (valueAsText ?? "").length > maxCollapsedLength;

  return (
    <div
      data-testid={`compare-panel-value-${index}-${attributeIndex}`}
      className={cn(
        "rounded-lg w-full min-h-[46px] px-2 py-1.5 flex flex-col",
        isPinned && attributeIndex % 2 !== 0 && "bg-teal-50",
        !isPinned && attributeIndex % 2 !== 0 && "bg-neutral-50"
      )}
    >
      <div
        className={cn(
          "text-sm flex items-center leading-[22px]",
          isPinned ? "text-teal-500" : "text-txtHeading",
          isGeometry
            ? "whitespace-pre-wrap break-words"
            : "truncate hover:text-clip py-2 hover:overflow-auto",
          !expanded && "line-clamp-2"
        )}
      >
        {valueAsText}
      </div>

      {shouldCollapse && (
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className={cn(
            "text-xs underline self-start my-1",
            isPinned ? "text-teal-500 fill-teal-500" : "text-txtHeading"
          )}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <span className="flex items-center gap-1">
            {expanded ? (
              <>
                Collapse <ArrowUpIcon width={16} height={16} />
              </>
            ) : (
              <>
                Expand <ArrowDownIcon width={16} height={16} />
              </>
            )}
          </span>
        </button>
      )}
    </div>
  );
}
